/** The full player-facing changelog. Entries live in src/data/updates.ts. */
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UPDATES, type UpdateTag } from '../data/updates';

const TAGS: UpdateTag[] = ['Army lists', 'Game modes', 'Rules accuracy', 'App'];

const controlButton = (active: boolean): React.CSSProperties => ({
  fontFamily: "'Cinzel', Georgia, serif",
  fontSize: '11px',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '6px 12px',
  cursor: 'pointer',
  border: `1px solid ${active ? 'var(--f-primary)' : 'var(--f-border)'}`,
  backgroundColor: active ? 'var(--f-primary)' : 'transparent',
  color: active ? '#fdf9ef' : 'var(--f-text-3)',
});

export default function Updates() {
  const [tag, setTag] = useState<UpdateTag | 'all'>('all');
  const [newestFirst, setNewestFirst] = useState(true);

  const shown = useMemo(() => {
    const filtered = tag === 'all' ? [...UPDATES] : UPDATES.filter((u) => u.tag === tag);
    filtered.sort((a, b) =>
      newestFirst ? b.isoDate.localeCompare(a.isoDate) : a.isoDate.localeCompare(b.isoDate),
    );
    return filtered;
  }, [tag, newestFirst]);

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '32px 20px 64px' }}>
      <p style={{
        fontFamily: "'Cinzel', Georgia, serif",
        fontSize: '8px',
        letterSpacing: '0.45em',
        textTransform: 'uppercase',
        color: 'var(--f-text-4)',
        marginBottom: '8px',
      }}>
        From the Camp
      </p>
      <h1 style={{
        fontFamily: "'Cinzel', Georgia, serif",
        fontSize: '22px',
        fontWeight: 700,
        color: 'var(--f-text)',
        marginBottom: '10px',
      }}>
        Updates
      </h1>
      <p style={{
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontSize: '14px',
        lineHeight: 1.7,
        color: 'var(--f-text-3)',
        marginBottom: '24px',
      }}>
        Everything that has changed in Battle Standard, in plain language — new army lists,
        game modes, and improvements to how the builder checks your armies against the books.
      </p>

      {/* Filter and sort controls */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        borderTop: '1px solid var(--f-border)',
        borderBottom: '1px solid var(--f-border)',
        padding: '12px 0',
        marginBottom: '28px',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <button style={controlButton(tag === 'all')} onClick={() => setTag('all')}>
            All
          </button>
          {TAGS.map((t) => (
            <button key={t} style={controlButton(tag === t)} onClick={() => setTag(t)}>
              {t}
            </button>
          ))}
        </div>
        <button
          style={controlButton(false)}
          onClick={() => setNewestFirst(!newestFirst)}
          title="Flip the date order"
        >
          {newestFirst ? 'Newest first ↓' : 'Oldest first ↑'}
        </button>
      </div>

      {shown.length === 0 && (
        <p style={{
          fontFamily: "'Source Serif 4', Georgia, serif",
          fontStyle: 'italic',
          fontSize: '14px',
          color: 'var(--f-text-3)',
        }}>
          No updates in this category yet.
        </p>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {shown.map((u, i) => (
          <li
            key={u.id}
            style={{
              borderTop: i === 0 ? 'none' : '1px solid var(--f-border)',
              paddingTop: i === 0 ? 0 : '24px',
              marginTop: i === 0 ? 0 : '24px',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
              marginBottom: '8px',
            }}>
              <span style={{
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--f-text-2)',
                borderLeft: `4px solid ${u.accent}`,
                paddingLeft: '9px',
              }}>
                {u.date}
              </span>
              <span style={{
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--f-text-2)',
                backgroundColor: 'var(--f-elevated)',
                border: '1px solid var(--f-text-4)',
                padding: '3px 9px',
              }}>
                {u.tag}
              </span>
            </div>
            <h2 style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '17px',
              fontWeight: 700,
              color: 'var(--f-text)',
              margin: '0 0 8px',
              letterSpacing: '0.02em',
            }}>
              {u.title}
            </h2>
            <p style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontSize: '14px',
              lineHeight: 1.65,
              color: 'var(--f-text-3)',
              margin: '0 0 8px',
            }}>
              {u.body}
            </p>
            {u.detail && (
              <ul style={{ margin: '0 0 10px', padding: '0 0 0 18px' }}>
                {u.detail.map((d, j) => (
                  <li key={j} style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontSize: '13px',
                    lineHeight: 1.6,
                    color: 'var(--f-text-3)',
                    marginBottom: '5px',
                  }}>
                    {d}
                  </li>
                ))}
              </ul>
            )}
            {u.to && u.cta && (
              <NavLink
                to={u.to}
                style={{
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '10px',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'var(--f-primary)',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                {u.cta} →
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
